import React, { useEffect, useState } from "react";
import ModalShell, { ModalLoading } from "../../../components/common/Modals/ModalShell";
import { V1ClockTime } from "../../../services/api/v1Mqtt.api";
import { CLOCK_ZONES, DEFAULT_CLOCK_ZONE, formatClock, wallClockIn } from "../../../services/mqtt/v1/v1Clock";

interface SetSpaClockModalProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    onSet: (time: V1ClockTime) => Promise<void>;
}

/**
 * Sets a V1 / Coldfire tub's clock to the current time in a chosen zone, as classic's
 * "Set Spa Time" does for both Yoctub and Coldfire. The time is taken when Set is pressed.
 */
const SetSpaClockModal: React.FC<SetSpaClockModalProps> = ({ open, setOpen, onSet }) => {
    // By label: two of classic's zones share an IANA zone.
    const [zoneLabel, setZoneLabel] = useState(DEFAULT_CLOCK_ZONE);
    const timeZone = CLOCK_ZONES.find((z) => z.label === zoneLabel)?.timeZone ?? 'Etc/GMT+6';
    const [now, setNow] = useState(() => new Date());
    const [sending, setSending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (!open) return;
        const timer = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(timer);
    }, [open]);

    const handleClose = () => {
        setError(null);
        setDone(false);
        setSending(false);
        setOpen(false);
    };

    const handleSet = async () => {
        setSending(true);
        setError(null);
        try {
            await onSet(wallClockIn(timeZone));
            setDone(true);
        } catch (e: any) {
            const reason = e?.response?.data?.data;
            setError(typeof reason === 'string' ? reason : "The spa's time could not be set.");
        } finally {
            setSending(false);
        }
    };

    const preview = formatClock(wallClockIn(timeZone, now)).full;

    return (
        <ModalShell
            open={open}
            onClose={handleClose}
            size="sm"
            busy={sending}
            tone={sending ? undefined : error ? "error" : done ? "success" : undefined}
            title={sending ? "Setting spa time…" : error ? "An error occurred" : done ? "Spa time sent" : "Set spa time"}
            subtitle={!sending && !error && !done ? "Sets the spa to the current time in the chosen time zone." : undefined}
            actions={sending ? undefined : (error || done) ?
                <button className="btn btn-primary" onClick={handleClose}>Done</button>
                :
                <>
                    <button className="btn" onClick={handleClose}>Cancel</button>
                    <button className="btn btn-primary" onClick={handleSet}>Set time</button>
                </>
            }
        >
            {sending ?
                <ModalLoading/>
                : error ?
                <p>{error}</p>
                : done ?
                <p>The spa will confirm its new time in a moment.</p>
                :
                <form className="field" onSubmit={(e) => { e.preventDefault(); handleSet(); }}>
                    <label className="field-label" htmlFor="spa-clock-zone">Time zone</label>
                    <select id="spa-clock-zone" className="input" value={zoneLabel} onChange={(e) => setZoneLabel(e.target.value)}>
                        {CLOCK_ZONES.map((z) => <option key={z.label} value={z.label}>{z.label}</option>)}
                    </select>
                    <p className="sd-muted">Will be set to {preview}</p>
                </form>
            }
        </ModalShell>
    );
};

export default SetSpaClockModal;
